import i18next from 'i18next';
import {
  Image,
  KenBurnsParams,
  LightboxItem,
  LightboxLanguageText,
  LightboxRootObject,
  Look,
  Lookbook,
  OwnedBy,
  Pane,
  PreviewParams,
  Thumbnail,
} from '@moment/net/softbox-types.ts';
import { IIabCategory } from '@pages/Root/services/api/lookBook/lookbook-model-definitions.ts';
import { RecirculationCardProps } from '@moment/components/RecirculationCard/RecirculationCard';

export interface GalleryItem {
  lookId: string;
  title: LightboxLanguageText;
  summary: LightboxLanguageText;
  credit: LightboxLanguageText | null;
  averageColor: string;
  kenBurnsParams: KenBurnsParams;
  previewParams: PreviewParams;
  images: Image[];
  thumbnails: Thumbnail[];
  ownedBy: OwnedBy;
  updatedAt: string; // ISO date string
  iabCategories: IIabCategory[];
  lbtype: string;
  sourceLink?: string;
}

export interface LookbookPaneMapping {
  lookUid: string;
  translations?: LightboxLanguageText;
}

export class EnhancedGalleryItem implements GalleryItem {
  averageColor: string;
  images: Image[];
  kenBurnsParams: KenBurnsParams;
  lookId: string;
  ownedBy: OwnedBy;
  previewParams: PreviewParams;
  summary: LightboxLanguageText;
  thumbnails: Thumbnail[];
  title: LightboxLanguageText;
  updatedAt: string;
  iabCategories: IIabCategory[];
  credit: LightboxLanguageText | null;
  lbtype: string;
  sourceLink?: string;

  constructor(item: GalleryItem) {
    this.averageColor = item.averageColor;
    this.images = item.images;
    this.kenBurnsParams = item.kenBurnsParams;
    this.lookId = item.lookId;
    this.ownedBy = item.ownedBy;
    this.previewParams = item.previewParams;
    this.summary = item.summary;
    this.thumbnails = item.thumbnails;
    this.title = item.title;
    this.updatedAt = item.updatedAt;
    this.iabCategories = item.iabCategories;
    this.credit = item.credit;
    this.lbtype = item.lbtype;
    this.sourceLink = item.sourceLink;
  }

  largestImage(): Image {
    const optimal = this.images.sort((a, b) => {
      return a.width - b.width;
    })[this.images.length - 1];
    if (optimal) {
      return optimal;
    }
    return this.images[0];
  }
}

export class GalleryItemFactory {
  private lookbook: Lookbook;
  private panesByLookUid: Map<string, Pane>;
  private defaultLanguage: string;
  private iabCategories: IIabCategory[];

  constructor(lookbook: Lookbook, defaultLanguage: string = 'en') {
    this.lookbook = lookbook;
    this.defaultLanguage = defaultLanguage;
    this.panesByLookUid = new Map();

    this.lookbook.plan?.views?.forEach((view) => {
      let singlePane: Pane = {};
      view.panes.forEach((pane) => {
        singlePane = Object.assign(singlePane, pane);
      });
      if (singlePane.lookUid) {
        this.panesByLookUid.set(singlePane.lookUid, singlePane);
      }
    });
    this.iabCategories = lookbook.interests?.map((i) => {
      return { code: i.uid, label: i.name };
    });
  }

  /**
   * Converts a simple string to a LightboxLanguageText object using the default language.
   * Handles undefined, null, or empty strings by returning an empty LightboxLanguageText object.
   */
  private convertStringToLightboxLanguageText(
    text: string | undefined | null
  ): LightboxLanguageText {
    if (!text || text.trim() === '') {
      return {}; // Return empty object for empty/null/undefined strings
    }
    return { [this.defaultLanguage]: text };
  }

  /**
   * Creates an empty LightboxLanguageText object.
   */
  private createEmptyLightboxLanguageText(): LightboxLanguageText {
    return {};
  }

  /**
   * Creates a single GalleryItem from a Look.
   * It determines the source of title and summary based on whether the look is the lookbook's cover
   * or if it's associated with a pane.
   */
  public createGalleryItem(
    look: Look,
    iabCategories: IIabCategory[],
    translations?: LightboxLanguageText,
    isCover: boolean = false
  ): EnhancedGalleryItem {
    let title: LightboxLanguageText;
    let summary: LightboxLanguageText;
    let credit: LightboxLanguageText | null;

    // Scenario 1: The Look is the cover look for the Lookbook.
    // Title and summary come directly from the Lookbook's properties.
    if (this.lookbook.coverUID === look.uid && isCover) {
      title = this.lookbook.title; // Assumed to be LightboxLanguageText
      summary = this.lookbook.summary; // Assumed to be LightboxLanguageText
      credit = this.lookbook.credit;
    } else {
      // Scenario 2: The Look is not the cover look.
      // Title comes from the Look's own title.
      // Summary comes from an associated Pane's translations, if available.
      title = this.convertStringToLightboxLanguageText(look.title);
      credit = null; // only want on cover page

      const associatedPane = this.panesByLookUid.get(look.uid);
      if (translations) {
        summary = translations;
      } else if (associatedPane && associatedPane.translations) {
        // The Pane provides the text details, which we'll use as the summary.
        summary = associatedPane.translations;
      } else {
        // No associated pane with translations, so the summary is empty.
        summary = this.createEmptyLightboxLanguageText();
      }
    }
    return new EnhancedGalleryItem({
      lookId: look.uid,
      title: title,
      summary: summary,
      averageColor: look.averageColor,
      kenBurnsParams: look.kenBurnsParams,
      previewParams: look.previewParams,
      images: look.images,
      thumbnails: look.thumbnails,
      ownedBy: look.ownedBy,
      updatedAt: look.updatedAt,
      iabCategories: iabCategories,
      credit: credit,
      lbtype: this.lookbook.lbtype,
      sourceLink: this.lookbook.sourceLink,
    });
  }

  public createCoverItem(
    look: Look,
    iabCategories: IIabCategory[]
  ): EnhancedGalleryItem {
    return this.createGalleryItem(
      look,
      iabCategories,
      this.createEmptyLightboxLanguageText(),
      true
    );
  }

  /**
   * Creates an array of GalleryItems for all looks in the Lookbook.
   */
  public createAllGalleryItems(): EnhancedGalleryItem[] {
    if (
      !this.lookbook.looks ||
      !this.lookbook.plan ||
      !this.lookbook.plan.views
    ) {
      return [];
    }

    // 1. Find the cover look and create its GalleryItem
    const coverLook = this.lookbook.looks.find((look) => {
      return look.uid === this.lookbook.coverUID;
    });
    const coverItem = coverLook
      ? [this.createCoverItem(coverLook, this.iabCategories)]
      : [];

    // 2. For the rest, map from plan.views[].panes, collecting lookUids in order
    const paneLookUids: LookbookPaneMapping[] = [];
    this.lookbook.plan.views.forEach((view) => {
      if (view.panes && Array.isArray(view.panes)) {
        // CMBU-2004 Decision made to no longer remove duplicate slides.
        if (view.panes.length > 0) {
          // panes are unordered and may not contain translations
          const lookUid = view.panes.find((p) => p.lookUid)?.lookUid;
          const translations = view.panes.find(
            (p) => p.translations
          )?.translations;
          if (lookUid) {
            paneLookUids.push({
              lookUid: lookUid,
              translations: translations,
            });
          }
        }
      }
    });

    // 3. For each lookUid, find the corresponding look (skip the coverUID if already included)
    const galleryItems: EnhancedGalleryItem[] = paneLookUids
      .map((lpm) => {
        const look = this.lookbook.looks.find((l) => {
          return l.uid === lpm.lookUid;
        });
        return look
          ? this.createGalleryItem(
              look,
              this.iabCategories,
              lpm.translations,
              false
            )
          : null;
      })
      .filter((item): item is EnhancedGalleryItem => {
        return item !== null;
      });

    // 4. Return cover first, then the rest
    return [...coverItem, ...galleryItems];
  }
}

export const createRecirculationCards = (
  recircResponse: LightboxRootObject
): RecirculationCardProps[] => {
  if (
    !recircResponse ||
    !recircResponse.items ||
    recircResponse.items.length === 0
  ) {
    return [];
  }

  return recircResponse.items.map((item: LightboxItem) => {
    const uid = item.uid;
    const owner = item.owner;
    const title =
      item.title[i18next.language] ||
      Object.values(item.title)[0] ||
      i18next.t('untitled');

    let category = i18next.t('general');
    if (item.interests && item.interests.length > 0) {
      const firstInterestId = item.interests[0];
      if (recircResponse.interests[firstInterestId]) {
        category =
          recircResponse.interests[firstInterestId].name[i18next.language] ||
          Object.values(recircResponse.interests[firstInterestId].name)[0] ||
          i18next.t('general');
      }
    }

    let imageUrl = '';
    if (item.previews && item.previews.length > 0) {
      imageUrl = item.previews[0].link;
    } else if (item.images && item.images.length > 0) {
      imageUrl = item.images[0].link;
    }

    return {
      uid,
      title,
      category,
      publisherLogo: item.brandLogo,
      publisherName: item.owner,
      darkPublisherLogo: item.brandLogoDark,
      image: imageUrl,
      link: item.link,
      owner,
    };
  });
};
